/**
 * Pro Shop runs on a page template, where Shopify ignores `?sort_by=` for
 * collection.products. Facets still re-renders the section after sort changes,
 * which paints the unsorted server HTML and overwrites any client reorder.
 *
 * Fix: short-circuit Section Rendering for this section, then reorder the
 * already-rendered cards from the URL's sort_by.
 */

import { sectionRenderer } from '@theme/section-renderer';

const CONTAINER_SELECTOR = '.product-grid-container--pro-shop';
const SECTION_ID_PREFIX = 'shopify-section-';

/**
 * @param {string | null} sortBy
 * @returns {string}
 */
function displayStatus(sortBy) {
  if (!sortBy) return '';

  const container = document.querySelector(CONTAINER_SELECTOR);
  const input = container?.querySelector(`input[name="sort_by"][value="${CSS.escape(sortBy)}"]`);
  if (input instanceof HTMLInputElement) {
    const name = input.dataset.optionName?.trim();
    if (name) return name;
  }

  const select = container?.querySelector('select[name="sort_by"]');
  if (select instanceof HTMLSelectElement) {
    const option = select.querySelector(`option[value="${CSS.escape(sortBy)}"]`);
    if (option?.textContent?.trim()) return option.textContent.trim();
  }

  return sortBy;
}

/**
 * @param {HTMLElement} a
 * @param {HTMLElement} b
 * @param {string} sortBy
 * @returns {number}
 */
function compareItems(a, b, sortBy) {
  const priceA = Number(a.dataset.sortPrice || 0);
  const priceB = Number(b.dataset.sortPrice || 0);
  const titleA = (a.dataset.sortTitle || '').toLowerCase();
  const titleB = (b.dataset.sortTitle || '').toLowerCase();
  const createdA = Number(a.dataset.sortCreated || 0);
  const createdB = Number(b.dataset.sortCreated || 0);
  const indexA = Number(a.dataset.sortIndex || 0);
  const indexB = Number(b.dataset.sortIndex || 0);

  switch (sortBy) {
    case 'price-ascending':
      return priceA - priceB || indexA - indexB;
    case 'price-descending':
      return priceB - priceA || indexA - indexB;
    case 'title-ascending':
      return titleA.localeCompare(titleB) || indexA - indexB;
    case 'title-descending':
      return titleB.localeCompare(titleA) || indexA - indexB;
    case 'created-ascending':
      return createdA - createdB || indexA - indexB;
    case 'created-descending':
      return createdB - createdA || indexA - indexB;
    default:
      return indexA - indexB;
  }
}

/**
 * @returns {string}
 */
function currentSortBy() {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get('sort_by');
  if (fromUrl) return fromUrl;

  const details = document.querySelector(`${CONTAINER_SELECTOR} details[data-default-sort-by]`);
  return details instanceof HTMLElement ? details.dataset.defaultSortBy || 'title-ascending' : 'title-ascending';
}

/**
 * @param {string} sortBy
 */
function sortProShopGrid(sortBy) {
  const grid = document.querySelector(`${CONTAINER_SELECTOR} .product-grid`);
  if (!(grid instanceof HTMLElement)) return;

  const items = Array.from(grid.querySelectorAll(':scope > .product-grid__item'));
  if (items.length === 0) return;

  items
    .sort((a, b) => compareItems(/** @type {HTMLElement} */ (a), /** @type {HTMLElement} */ (b), sortBy))
    .forEach((item) => grid.appendChild(item));
}

/**
 * @param {string} sortBy
 */
function syncSortUI(sortBy) {
  const container = document.querySelector(CONTAINER_SELECTOR);
  if (!container) return;

  container.querySelectorAll('input[name="sort_by"]').forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;
    input.checked = input.value === sortBy;
    const option = input.closest('[role="option"]');
    if (option instanceof HTMLElement) {
      option.setAttribute('aria-selected', input.checked ? 'true' : 'false');
      option.tabIndex = input.checked ? 0 : -1;
    }
  });

  container.querySelectorAll('select[name="sort_by"]').forEach((select) => {
    if (!(select instanceof HTMLSelectElement)) return;
    select.value = sortBy;
  });

  const statusText = displayStatus(sortBy);
  container.querySelectorAll('.sorting-filter .facets__status').forEach((status) => {
    status.textContent = statusText;
    status.classList.remove('desktop:hidden');
  });
}

function applyClientSort() {
  const sortBy = currentSortBy();
  sortProShopGrid(sortBy);
  syncSortUI(sortBy);
}

/**
 * @param {string} sectionId
 * @returns {boolean}
 */
function isProShopSection(sectionId) {
  const normalized = sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
  const sectionEl = document.getElementById(`${SECTION_ID_PREFIX}${normalized}`);
  return Boolean(sectionEl?.querySelector(CONTAINER_SELECTOR));
}

function patchSectionRenderer() {
  const originalRenderSection = sectionRenderer.renderSection.bind(sectionRenderer);

  sectionRenderer.renderSection = async (sectionId, options) => {
    if (isProShopSection(sectionId)) {
      // Facets already updated the URL. Skip the useless page-template re-fetch
      // (server ignores sort_by) and reorder the cards we already have.
      applyClientSort();
      const normalized = sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
      return document.getElementById(`${SECTION_ID_PREFIX}${normalized}`)?.outerHTML ?? '';
    }

    return originalRenderSection(sectionId, options);
  };
}

function initProShopSort() {
  if (!document.querySelector(CONTAINER_SELECTOR)) return;

  patchSectionRenderer();
  applyClientSort();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProShopSort, { once: true });
} else {
  initProShopSort();
}
